import React, { useContext, useEffect, useState } from "react";
import { Button, Card, CardBody, CardTitle, Col, Input, Row } from "reactstrap";
import NumberFormat from "react-number-format";
import Select from "react-select";
import { toast } from "react-toastify";
import _ from "lodash";
import { ServiceThreadSpinner } from ".";
import { SpinnerButton } from './';
import { api, dateHelpers, helpers, UserContext } from "../utils";

export default function AccountInformation(props) {
   const { currentUser } = useContext(UserContext);
   const [accountInfo, setAccountInfo] = useState({});
   const [customerId, setCustomerId] = useState(props.customerId);
   const [editCredit, setEditCredit] = useState(false);
   const [editUPS, setEditUPS] = useState(false);
   const [loaded, setLoaded] = useState(false);
   const [newLimit, setLimit] = useState(0);
   const [newTerm, setNewTerm] = useState("");
   const [termsList, setTermsList] = useState([]);
   const [upsAccount, setUPSAccount] = useState(props.upsAccount);
   const [working, setWorking] = useState(false);
   const canEdit = currentUser && (currentUser.isAdmin || currentUser.isPowerUser);

   useEffect(() => {
      getPayTerms();
   }, []);

   useEffect(() => {
      if (props.customerId !== customerId) {
         setCustomerId(props.customerId);
         setLoaded(false);
      }

      if (!loaded) {
         // Query the account this card was handed (the dashboard already resolved the active account),
         // never the user's primary - matches the heading fix and the sibling cards.
         var custId = customerId;

         api
            .fetch(`Customer/GetAccountInfo/${custId}`)
            .then((response) => {
               if (response.data?.success) {
                  setAccountInfo(response.data.accountInfo);
               }
               setLoaded(true);
            })
            .catch(api.catchHandler);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [loaded, props.customerId]);

   useEffect(() => {
      setUPSAccount(accountInfo?.upsAccount ?? '');
      setLimit(accountInfo?.creditLimit ?? 0);
      setNewTerm(accountInfo?.paymentTerms ?? "");
      setEditCredit(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [accountInfo]);

   function getPayTerms() {
      api
         .fetch(`Reference/GetPaymentTerms`)
         .then((r) => {
            if (r.data?.success) {
               setTermsList(r.data.payTerms);
            } else {
               toast.error(r.data.message);
            }
         })
         .catch((error) => {
            toast.error(error);
            api.catchHandler();
         });
   }

   function handleUPSAccountSave() {
      const payload = {
         upsAccount: upsAccount,
         customerId: customerId,
      };
      setLoaded(false);
      api
         .post(`Customer/SaveUPSAccountById/${customerId}`, payload)
         .then((r) => {
            if (r.data.success) {
               setEditUPS(false);
               setLoaded(true);
            } else {
               // toast.error(r.data.message);
            }
         })
         .catch((err) => {
            api.catchHandler(err);
         });
   }

   function saveCreditChanges() {
      if (newTerm === "CBD" && newLimit > 0) {
         toast.error("CBD customers are not allowed a credit limit");
         return;
      }

      setWorking(true);
      let pay = {
         CreditLimit: newLimit,
         CustomerId: customerId,
         PayTerms: newTerm
      }

      api
         .post(`Customer/SaveCustomerChanges`, pay)
         .then((r) => {
            if (r.data?.success) {
               setAccountInfo(r.data.accountInfo);
            } else {
               toast.error(r.data.message);
            }
         })
         .catch((error) => {
            toast.error(error);
            api.catchHandler();
         })
         .finally(() => {
            setWorking(false)
         });
   }

   function toggleEdit() {
      if (editCredit === true) {
         // editing is being canceled, so reset to original limit and pay terms
         setLimit(accountInfo?.creditLimit ?? 0);
         setNewTerm(accountInfo?.paymentTerms ?? "");
      }
      setEditCredit(!editCredit);
   }

   return (
      <>
         <div>
            <span className="h2 me-2">Account Information</span>
            {/* Always label the account actually being displayed (the customerId passed in), never the
                user's primary. Under multi-account those differ, and showing the primary over another
                assigned account's data mislabels it. */}
            <span className="text-muted">Customer #{customerId}</span>
            {canEdit === true ? (
               <span>
                  <SpinnerButton
                     className="shredButton mx-2 px-1 me-2 width80px"
                     title={editCredit ? "Cancel" : "Edit"}
                     children={editCredit ? "Cancel" : "Edit"}
                     onClick={toggleEdit}
                     on={working}
                     disabled={working}
                  />
                  {editCredit === true ? (
                     <SpinnerButton
                        className="shredButton mx-0 px-1 width80px"
                        title="Save"
                        children="Save"
                        onClick={saveCreditChanges}
                        on={working}
                        disabled={working}
                     />
                  ) : null}
               </span>
            ): null}
            <span style={{ float: "right", marginTop: ".5rem", paddingRight: "2rem" }}>
               Default UPS Acount: {accountInfo?.upsAccount ?? ''}
            </span>
         </div>
         <div>
            {editUPS === true ? (
               <Row className="d-flex justify-content-end" style={{ marginTop: ".5rem" }} >
                  <Col className="d-flex justify-content-end" xs="2">
                     <Input
                        style={{ width: "56%" }}
                        type="text"
                        name="newUps"
                        value={upsAccount}
                        onChange={(e) => setUPSAccount(e.target.value)}
                     ></Input>
                  </Col>
                  <Col xs="1">
                     <SpinnerButton
                        className="shredButton btn-md"
                        onClick={() => handleUPSAccountSave(upsAccount)}
                        title="SaveUPS"
                        children="Save"
                        on={!loaded}
                        disabled={!loaded}
                     />
                  </Col>
               </Row>
            ) : null}
         </div>

         <hr />
         <ServiceThreadSpinner on={!loaded || working}>
            <Row className="px-4">
               <Col xs={3} className="px-0">
                  {canEdit === false || editCredit === false ? (
                     <Row>
                        <p className="m-1">
                           <strong>Credit Limit: </strong>
                           {helpers.formatCurrency(accountInfo?.creditLimit ?? 0)}
                        </p>
                     </Row>
                  ): null}
                  {canEdit === true && editCredit === true ? (
                     <Row>
                        <Col className="width135px">
                           <p className="m-1">
                              <strong>Credit Limit: </strong>
                           </p>
                        </Col>
                        <Col>
                           <NumberFormat
                              className="form-control no-spinner mx-0 width135px"
                              value={newLimit}
                              onValueChange={({ floatValue }) => {
                                 if (floatValue > 9999999) floatValue = 9999999;
                                 if (floatValue.toString().match(/^\d{1,8}(\.\d{0,2})?$/)) // One to eight digits with 2 decimals allowed
                                    setLimit(floatValue);
                              }}
                              allowNegative={false}
                           />
                        </Col>
                     </Row>
                  ): null}
                  <Row>
                     <p className="m-1">
                        <strong>Credit Available: </strong>
                        {helpers.formatCurrency(accountInfo?.creditAvailable ?? 0)}
                     </p>
                  </Row>
                  <Row>
                     {canEdit === false || editCredit === false || termsList.length <= 0 ? (
                        <p className="m-1">
                           <strong>Credit Terms: </strong>
                           {accountInfo?.paymentTerms}
                        </p>
                     ): null}
                     {canEdit === true && editCredit === true && termsList.length > 0 ? (
                        <Row>
                           <Col className="width135px">
                              <p className="m-1">
                                 <strong>Credit Terms: </strong>
                              </p>
                           </Col>
                           <Col>
                              <Select
                                 classNamePrefix="serviceThreadSelect"
                                 className="flex-fill"
                                 options={termsList}
                                 value={_.find(termsList, (pt) => pt.value === newTerm)}
                                 onChange={(opt) => setNewTerm(opt.value)}
                              />
                           </Col>
                        </Row>
                     ): null}
                  </Row>
                  <Row>
                     <p className="m-1">
                        <strong>Current Balance: </strong>
                        {helpers.formatCurrency(accountInfo?.customerBalance)}
                     </p>
                  </Row>
                  <Row>
                     <p className="m-1">
                        <strong>Balance 0-30: </strong>
                        {helpers.formatCurrency(accountInfo?.balanceThirtyDays)}
                     </p>
                  </Row>
                  <Row>
                     <p className="m-1">
                        <strong>Balance 31-60: </strong>
                        {helpers.formatCurrency(accountInfo?.balanceThirtySixty)}
                     </p>
                  </Row>
                  <Row>
                     <p className="m-1">
                        <strong>Balance 61-90: </strong>
                        {helpers.formatCurrency(accountInfo?.balanceSixtyNinety)}
                     </p>
                  </Row>
                  <Row>
                     <p className={(accountInfo?.balanceNinetyPlus === 0) ? "m-1" : "m-1 text-red"}>
                        <strong>Balance 90+: </strong>
                        {helpers.formatCurrency(accountInfo?.balanceNinetyPlus)}
                     </p>
                  </Row>
               </Col>

               <Col xs={3}>
                  <Row>
                     <p className="m-1">
                        <strong>YTD Purchases: </strong>
                        {helpers.formatCurrency(accountInfo?.yearToDateSales)}
                     </p>
                  </Row>
                  {currentUser && !currentUser.isAnyCustomerTypeRole ? (
                     <>
                        <Row>
                           <p className="m-1">
                              <strong>Last YTD Purchases: </strong>
                              {helpers.formatCurrency(accountInfo?.priorYTDSales)}
                           </p>
                        </Row>
                        <Row>
                           <p className="m-1">
                              <strong>YTD Change: </strong>
                              {helpers.formatCurrency(accountInfo?.yearToDateSales - accountInfo?.priorYTDSales)}
                           </p>
                        </Row>
                        <Row>
                           <p className="m-1">
                              <strong>% Change: </strong>
                              {helpers.formatPercent(accountInfo?.percentChange, 2)}
                           </p>
                        </Row>
                     </>
                  ) : null}
                  <Row>
                     <p className="m-1">
                        <strong>Last Payment Date: </strong>
                        {dateHelpers.toLongDateString(accountInfo?.lastPaymentDate)}
                     </p>
                  </Row>
                  <Row>
                     <p className="m-1">
                        <strong>Last Payment Amount: </strong>
                        {helpers.formatCurrency(accountInfo?.lastPaymentAmount)}
                     </p>
                  </Row>
                  <Row>
                     <p className={(accountInfo?.lateBalance === 0) ? "m-1" : "m-1 text-red"}>
                        <strong>Late Balance: </strong>
                        {helpers.formatCurrency(accountInfo?.lateBalance)}
                     </p>
                  </Row>
               </Col>

               <Col xs={3} className="px-0">
                  <Row>
                     <p className="m-1">
                        <strong>Distributor: </strong>
                        {helpers.booleanToYesNo(accountInfo?.isDistributor)}
                     </p>
                  </Row>
                  {currentUser && !currentUser.isAnyCustomerTypeRole ? (
                     <>
                        <Row>
                           <p className="m-1">
                              <strong>Customer Status: </strong>
                              {accountInfo?.formatedCustomerStatus}
                           </p>
                        </Row>
                        <Row>
                           <p className="m-1">
                              <strong>Customer Since: </strong>
                              {dateHelpers.toLongDateString(accountInfo?.createdDate)}
                           </p>
                        </Row>
                     </>
                  ) : null}
               </Col>

               <Col xs={3}>
                  {/* Customers can't change the default UPS account (SaveUPSAccountById is internal-only). Jay may ask
                      for this back; if so, open that action to customers (it already calls CleanCriteria) and show the
                      button again. */}
                  {currentUser && !currentUser.isAnyCustomerTypeRole ? (
                     <Row>
                        <Col xs={12}>
                           {editUPS === false ? (
                              <Button onClick={() => setEditUPS(true)} className={"shredButton w-100 btn-sm"} >
                                 EDIT DEFAULT UPS ACCOUNT
                              </Button>
                           ) : (
                              <Button onClick={() => setEditUPS(false)} className={"w-100 btn-sm"} >
                                 Cancel
                              </Button>
                           )}
                        </Col>
                     </Row>
                  ) : null}
                  <Card style={{ backgroundColor: "#eee" }}>
                     <CardBody>
                        <CardTitle className="accountInfoProfileTitle">
                           SALES CONTACT
                        </CardTitle>
                        <Row>
                           <Col xs={3}>
                              <img
                                 src={
                                    process.env.PUBLIC_URL +
                                    (accountInfo?.salespersonInfo &&
                                       accountInfo?.salespersonInfo.photo)
                                 }
                                 alt="Salesperson"
                                 style={{ width: "100%", minWidth: "5em" }}
                              />
                           </Col>
                           <Col xs={9}>
                              <strong>
                                 {accountInfo?.salespersonInfo &&
                                    `${_.capitalize(
                                       accountInfo?.salespersonInfo.firstName
                                    )} ${_.capitalize(
                                       accountInfo?.salespersonInfo.lastName
                                    )}`}
                              </strong>
                              <br />
                              <a
                                 href={`mailto:${accountInfo?.salespersonInfo &&
                                    accountInfo?.salespersonInfo.email
                                    }`}
                                 className="shredLink"
                              >
                                 {accountInfo?.salespersonInfo &&
                                    accountInfo?.salespersonInfo.email}
                              </a>
                              <br />
                              <span>{`w: ${accountInfo?.salespersonInfo &&
                                 helpers.formatPhoneNumber(accountInfo?.salespersonInfo.phone)
                                 }`}</span>
                              <br />
                              <span>{`c: ${accountInfo?.salespersonInfo &&
                                 helpers.formatPhoneNumber(accountInfo?.salespersonInfo.cell)
                                 }`}</span>
                              <br />
                           </Col>
                        </Row>
                     </CardBody>
                  </Card>
               </Col>
            </Row>
         </ServiceThreadSpinner>
      </>
   );
}
